<template>
  <div ref="element" class="card stroke-m neutral-50 outlined">
    <div v-if="controls" class="controls">
      <button
        type="button"
        class="button-close"
        aria-label="close"
        @click="close"
      >
        <icon name="material-symbols-light:close-small-rounded" size="2.4rem" />
      </button>
    </div>

    <h2 v-if="title" class="title">{{ title }}</h2>
    <div class="scrollable">
      <div class="content">
        <slot />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
interface Props {
  title?: string;
  controls?: boolean;
  closeClickOutside?: boolean;
  scrollable?: boolean;
  closeTimer?: number;
}

const props = defineProps({
  title: { default: '' },
  controls: { type: Boolean, default: true },
  closeClickOutside: { type: Boolean, default: true },
  scrollable: { type: Boolean, default: true },
  closeTimer: { default: -1 }
});

const element = ref(null);

useMathJax(element, (el) => {
  el.classList.add('show');
});

const emit = defineEmits(["close"]);

const { close } = useDialogControls(element, props, emit);

const cssoverflow = computed(() => (props.scrollable ? 'auto' : 'hidden'));
</script>
<style lang="scss" scoped>
.card {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  min-width: 30rem;
  max-width: 50rem;
  max-height: calc(
    100vh - var(--header_height) - var(--row_gap) - var(--row_gap)
  );
  max-height: calc(
    100dvh - var(--header_height) - var(--row_gap) - var(--row_gap)
  );
  padding: var(--gap_l);
  box-shadow: rgb(0 0 0 / 16%) 0 10px 36px 0, rgb(0 0 0 / 6%) 0 0 0 1px;
  justify-self: center;
  gap: var(--gap_l);

  --gap_elements: 6rem;

  .scrollable {
    width: calc(100% + 1.8rem);
    min-height: 10rem;
    margin-left: 1.8rem;
    padding-right: 1.8rem;
    overflow-y: v-bind(cssoverflow);
    transition: opacity 300ms $ease-out-quad;
    opacity: 0;
    &::-webkit-scrollbar {
      width: 0.5rem;
    }
    &::-webkit-scrollbar-thumb,
    &::-webkit-scrollbar-track {
      border-radius: 0.25rem;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color_neutral_100);
    }

    &::-webkit-scrollbar-track {
      background: var(--color_neutral_60);
    }

    .content {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: var(--gap_l);
    }
  }
  &.show {
    .scrollable {
      opacity: 1;
    }
  }
}

.title {
  text-align: center;
  @include font('xl', 700, 'secondary');
}

.controls {
  display: flex;
  position: absolute;
  z-index: 2;
  top: 0.5rem;
  right: 0.5rem;
  flex-direction: row-reverse;
  width: 100%;
}
.button-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  overflow: hidden;
  transform: translate(0, 0);
  transition: transform 500ms $ease-out-quad, box-shadow 200ms $ease-in-quad;
  border-radius: 50%;
  background-color: var(--color_error);
  color: var(--color_neutral_25);
  &:hover,
  &:focus {
    transform: rotate(180deg);
  }
}
</style>
